/**
 * `/settings/budgets` — spending budget + spend history.
 *
 * The budget is denominated in credits (the unit the wallet enforces)
 * per billing cycle. Soft = threshold alerts only; hard = paid usage
 * blocks once the budget is spent (the API answers 402
 * `BUDGET_EXCEEDED`). Alert thresholds are per-workspace percentages
 * delivered through the existing notification channels (email, Slack,
 * webhooks — Settings → Webhooks).
 *
 * Everything here is org-scoped and admin-gated for writes; members
 * see the meter + history with the form disabled, mirroring
 * `settings/workspace.tsx`.
 */
import { ConfirmDialog } from '@/components/primitives/ConfirmDialog';
import { EmptyState } from '@/components/primitives/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Meter, MeterIndicator, MeterTrack } from '@/components/ui/meter';
import {
  NumberField,
  NumberFieldDecrement,
  NumberFieldGroup,
  NumberFieldIncrement,
  NumberFieldInput,
} from '@/components/ui/number-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toastFromError, toastSuccess } from '@/lib/errors';
import { formatCents, relativeTime } from '@/lib/format';
import type { BudgetMode, LedgerEntry } from '@/lib/queries';
import {
  useActiveOrg,
  useBilling,
  useBudget,
  useLedger,
  useSetBudgetMutation,
  useWorkspace,
} from '@/lib/queries';
import { cn } from '@/lib/utils';
import { BUDGET_ALERT_MAX_THRESHOLDS } from '@clickweave/shared';
import { Wallet, X } from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { type ReactElement, useEffect, useState } from 'react';

export const Route = createFileRoute('/_app/settings/budgets')({
  component: BudgetsSettings,
});

function BudgetsSettings(): ReactElement {
  const budget = useBudget();
  const workspace = useWorkspace();
  const { orgId } = useActiveOrg();
  const billing = useBilling();
  const setBudget = useSetBudgetMutation();

  const activeOrg = workspace.data?.organisations.find((o) => o.auth_provider_id === orgId);
  const isAdmin = activeOrg?.role === 'admin';

  // Form state, seeded from the server row (same pattern as the
  // other settings pages: local state + explicit Save).
  const [mode, setMode] = useState<BudgetMode>('off');
  const [credits, setCredits] = useState<number | null>(null);
  const [thresholds, setThresholds] = useState<number[]>([80, 90]);
  const [thresholdDraft, setThresholdDraft] = useState<number | null>(null);
  const [confirmOff, setConfirmOff] = useState(false);

  useEffect(() => {
    if (!budget.data) return;
    setMode(budget.data.mode);
    setCredits(budget.data.budget_credits);
    setThresholds(budget.data.alert_thresholds);
  }, [budget.data]);

  if (budget.isLoading || workspace.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full max-w-2xl" />
        <Skeleton className="h-48 w-full max-w-2xl" />
      </div>
    );
  }

  if (billing.plan === null && !billing.isLoading) {
    return (
      <EmptyState
        icon={Wallet}
        title="Budgets need an active plan"
        description="Spending budgets cap the credits your workspace uses each billing cycle. Pick a plan first — then set a budget here."
        action={
          <Button render={<Link to="/settings/billing" />} variant="outline">
            Go to Billing
          </Button>
        }
      />
    );
  }

  const data = budget.data;
  const serverMode = data?.mode ?? 'off';
  const dirty =
    mode !== serverMode ||
    (mode !== 'off' && credits !== (data?.budget_credits ?? null)) ||
    thresholds.join(',') !== (data?.alert_thresholds ?? []).join(',');
  const canSave = isAdmin && dirty && (mode === 'off' || (credits !== null && credits > 0));

  const save = (): void => {
    setBudget.mutate(
      {
        mode,
        ...(mode !== 'off' && credits ? { budget_credits: credits } : {}),
        alert_thresholds: thresholds,
      },
      {
        onSuccess: () => {
          setConfirmOff(false);
          toastSuccess(mode === 'off' ? 'Budget turned off' : 'Budget saved');
        },
        onError: (err) => toastFromError(err),
      },
    );
  };

  const onSave = (): void => {
    // Turning an active budget off removes the guardrail — confirm.
    if (mode === 'off' && serverMode !== 'off') {
      setConfirmOff(true);
      return;
    }
    save();
  };

  const addThreshold = (): void => {
    if (thresholdDraft === null) return;
    const value = Math.round(thresholdDraft);
    if (value < 1 || value > 100 || thresholds.includes(value)) return;
    if (thresholds.length >= BUDGET_ALERT_MAX_THRESHOLDS) return;
    setThresholds([...thresholds, value].sort((a, b) => a - b));
    setThresholdDraft(null);
  };

  return (
    <div className="max-w-2xl space-y-8">
      {data && data.mode !== 'off' && data.budget_credits !== null && (
        <SpendMeter
          spent={data.spent_this_period_credits}
          budget={data.budget_credits}
          topThreshold={Math.max(...data.alert_thresholds, 0)}
          cycleExpiresAt={data.cycle_expires_at}
        />
      )}

      <section className="space-y-4">
        <header>
          <h2 className="text-base font-semibold text-[var(--color-ink)]">Spending budget</h2>
          <p className="text-sm text-[var(--color-mute)]">
            Cap the credits this workspace can use each billing cycle.{' '}
            {isAdmin ? '' : 'Only admins can edit.'}
          </p>
        </header>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field>
            <FieldLabel htmlFor="budget-mode">Mode</FieldLabel>
            <Select
              value={mode}
              onValueChange={(v) => setMode(v as BudgetMode)}
              disabled={!isAdmin}
            >
              <SelectTrigger id="budget-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="off">Off — no budget</SelectItem>
                <SelectItem value="soft">Soft — alert only</SelectItem>
                <SelectItem value="hard">Hard — block further usage</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-[var(--color-mute)]">
              {mode === 'hard'
                ? 'Audits, monitors, and AI pause once the budget is spent, until the next cycle or a higher budget.'
                : mode === 'soft'
                  ? 'You get alerts at your thresholds; usage continues past the budget.'
                  : 'No cap — usage is bounded only by your credits and overage settings.'}
            </p>
          </Field>
          {mode !== 'off' && (
            <Field>
              <FieldLabel htmlFor="budget-credits">Budget per billing cycle</FieldLabel>
              <NumberField
                id="budget-credits"
                value={credits}
                onValueChange={(v) => setCredits(v)}
                min={1}
                step={100}
                disabled={!isAdmin}
              >
                <NumberFieldGroup>
                  <NumberFieldDecrement />
                  <NumberFieldInput placeholder="1000" />
                  <NumberFieldIncrement />
                </NumberFieldGroup>
              </NumberField>
              <p className="text-xs text-[var(--color-mute)]">
                In credits.
                {credits && data
                  ? ` ≈ ${formatCents(credits * data.cents_per_credit)} if drawn entirely as overage.`
                  : ''}
              </p>
            </Field>
          )}
        </div>

        {mode !== 'off' && (
          <Field>
            <FieldLabel htmlFor="budget-threshold-input">Alert thresholds</FieldLabel>
            <div className="flex flex-wrap items-center gap-2">
              {thresholds.map((t) => (
                <Badge key={t} variant="outline" className="gap-1 tabular-nums">
                  {t}%
                  {isAdmin && (
                    <button
                      type="button"
                      aria-label={`Remove ${t}% threshold`}
                      className="text-[var(--color-mute)] hover:text-[var(--color-ink)]"
                      onClick={() => setThresholds(thresholds.filter((x) => x !== t))}
                    >
                      <X size={12} />
                    </button>
                  )}
                </Badge>
              ))}
              {isAdmin && thresholds.length < BUDGET_ALERT_MAX_THRESHOLDS && (
                <div className="flex items-center gap-2">
                  <NumberField
                    id="budget-threshold-input"
                    className="w-28"
                    value={thresholdDraft}
                    onValueChange={(v) => setThresholdDraft(v)}
                    min={1}
                    max={100}
                    step={5}
                  >
                    <NumberFieldGroup>
                      <NumberFieldInput placeholder="%" />
                    </NumberFieldGroup>
                  </NumberField>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addThreshold}
                    disabled={thresholdDraft === null}
                  >
                    Add
                  </Button>
                </div>
              )}
            </div>
            <p className="text-xs text-[var(--color-mute)]">
              Up to {BUDGET_ALERT_MAX_THRESHOLDS} percentages. Alerts go to your workspace's
              notification channels — email, Slack, and webhooks.
            </p>
          </Field>
        )}

        {isAdmin && (
          <Button onClick={onSave} disabled={!canSave} loading={setBudget.isPending}>
            Save budget
          </Button>
        )}
      </section>

      <div className="h-px w-full bg-[var(--color-line)]" />

      <SpendHistory />

      <ConfirmDialog
        open={confirmOff}
        onOpenChange={setConfirmOff}
        title="Turn off the spending budget?"
        description="Usage will no longer be capped or alerted on. Your credit balance and overage settings still apply."
        confirmLabel="Turn off"
        destructive
        loading={setBudget.isPending}
        onConfirm={save}
      />
    </div>
  );
}

/**
 * Current-cycle spend vs budget. Indicator turns amber past the top
 * alert threshold and red at 100% — same severity language as the
 * alert emails.
 */
function SpendMeter({
  spent,
  budget,
  topThreshold,
  cycleExpiresAt,
}: {
  spent: number;
  budget: number;
  topThreshold: number;
  cycleExpiresAt: string | null;
}): ReactElement {
  const percent = Math.min(100, (spent / budget) * 100);
  const over = spent / budget >= 1;
  const nearing = !over && topThreshold > 0 && (spent / budget) * 100 >= topThreshold;
  return (
    <section className="space-y-2 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <div className="flex items-baseline justify-between">
        <p className="text-sm font-medium text-[var(--color-ink)]">This billing cycle</p>
        <p className="text-sm tabular-nums text-[var(--color-mute)]">
          <span className={cn('font-semibold', over && 'text-[var(--color-danger)]')}>
            {spent.toLocaleString()}
          </span>{' '}
          / {budget.toLocaleString()} credits
        </p>
      </div>
      <Meter value={percent} aria-label="Budget used">
        <MeterTrack className="rounded-full">
          <MeterIndicator
            className={cn(
              'rounded-full',
              over ? 'bg-[var(--color-danger)]' : nearing ? 'bg-[var(--color-warning)]' : undefined,
            )}
          />
        </MeterTrack>
      </Meter>
      <p className="text-xs text-[var(--color-mute)]">
        {cycleExpiresAt
          ? `Resets when your plan cycle renews ${relativeTime(cycleExpiresAt)}.`
          : 'Resets when your plan cycle renews.'}
      </p>
    </section>
  );
}

const KIND_LABEL: Record<LedgerEntry['kind'], string> = {
  grant_cycle: 'Plan credits',
  grant_trial: 'Trial credits',
  topup: 'Top-up',
  drawdown: 'Usage',
  expiry: 'Expired',
  refund: 'Refund',
  adjustment: 'Adjustment',
};

/**
 * Credit-ledger table with keyset Prev/Next. `cursors` is the stack
 * of `next_cursor` values we've paged past — Next pushes, Prev pops.
 */
function SpendHistory(): ReactElement {
  const [cursors, setCursors] = useState<number[]>([]);
  const before = cursors[cursors.length - 1] ?? null;
  const ledger = useLedger(before);
  const entries = ledger.data?.entries ?? [];

  return (
    <section className="space-y-3">
      <header>
        <h2 className="text-base font-semibold text-[var(--color-ink)]">Spend history</h2>
        <p className="text-sm text-[var(--color-mute)]">
          Every credit grant, drawdown, and refund on this workspace, newest first.
        </p>
      </header>
      {ledger.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : entries.length === 0 && cursors.length === 0 ? (
        <p className="rounded-xl border border-dashed border-[var(--color-line)] px-4 py-6 text-center text-sm text-[var(--color-mute)]">
          No credit activity yet — history appears once your first credits are granted.
        </p>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Entry</TableHead>
                <TableHead className="text-right">Credits</TableHead>
                <TableHead className="text-right">Balance after</TableHead>
                <TableHead className="text-right">When</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>
                    <span className="font-medium text-[var(--color-ink)]">
                      {KIND_LABEL[e.kind]}
                    </span>
                    {e.action && (
                      <span className="ml-1.5 text-xs text-[var(--color-mute)]">{e.action}</span>
                    )}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'text-right tabular-nums',
                      e.delta < 0 ? 'text-[var(--color-mute)]' : 'text-[var(--color-success)]',
                    )}
                  >
                    {e.delta > 0 ? `+${e.delta.toLocaleString()}` : e.delta.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {e.balance_after.toLocaleString()}
                  </TableCell>
                  <TableCell
                    className="text-right text-[var(--color-mute)]"
                    title={new Date(e.created_at).toLocaleString()}
                  >
                    {relativeTime(e.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {(ledger.data?.next_cursor != null || cursors.length > 0) && (
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                variant="outline"
                size="sm"
                disabled={cursors.length === 0}
                onClick={() => setCursors((c) => c.slice(0, -1))}
              >
                ← Newer
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={ledger.data?.next_cursor == null}
                onClick={() => {
                  const next = ledger.data?.next_cursor;
                  if (next != null) setCursors((c) => [...c, next]);
                }}
              >
                Older →
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
